/**
 * Snapshot ring buffer + interpolation clock.
 *
 * See `wp6-client-render.md` §"Snapshot buffer & render clock".
 * PURE — no Pixi, no DOM, no rAF. The clock is DRIVEN by wall-clock
 * `dt` handed in from outside (rAF in the browser, `performance.now`
 * deltas in tests / harnesses).
 *
 * Doctrine:
 *   • Ring buffer capacity 64, ordered ascending by tick, oldest drops
 *     first once full.
 *   • Rendering time = `latestTick - interpolationDelayTicks`, expressed
 *     as a FLOAT `renderTick` advanced by wall-clock dt each frame.
 *   • Soft resync toward the target each frame; SNAP if drift > 3
 *     ticks or on visibility change.
 *   • Interpolation pair: `a.tick ≤ renderTick ≤ b.tick`, adjacent
 *     ticks in the buffer. Missing pair (gap or pre-start): clamp to
 *     the nearest single snapshot. NO EXTRAPOLATION EVER.
 */

import type { Tick, WireSimState } from "@pit/protocol";
export { applyEntityDelta, applyWireDelta } from "@pit/protocol";

/** Immutable entry in the ring — snapshot with its wire tick. */
export interface SnapshotEntry {
  readonly tick: Tick;
  readonly state: WireSimState;
}

/** Maximum snapshots retained. Doctrine value. */
export const RING_CAPACITY = 64;

/** Default rendering delay behind `latestTick`, in ticks. Overridable
 *  from tuning at construction time. Doctrine default: 2. */
export const DEFAULT_INTERP_DELAY_TICKS = 2;

/** Snap threshold — clock error above this jumps rather than nudges. */
export const SNAP_ERROR_TICKS = 3;

/** Per-frame nudge factor when we're within the snap threshold. */
export const RESYNC_NUDGE_FRACTION = 0.1;

/**
 * Ring buffer of snapshots ordered by tick.
 *
 * PUSH semantics:
 *   • Duplicate tick (same as latest) → dropped (idempotent).
 *   • Older tick than newest → dropped (stale; server is authoritative).
 *   • At capacity → oldest entry evicted.
 *
 * All fields private; the class is small enough that method-level
 * tests cover it exhaustively.
 */
export class SnapshotBuffer {
  private readonly entries: SnapshotEntry[] = [];

  /**
   * Push a snapshot. Returns `true` if accepted, `false` if dropped
   * (stale or duplicate). Callers can use the boolean for logging;
   * silent drop is fine for the renderer.
   */
  push(entry: SnapshotEntry): boolean {
    const latest = this.latest();
    if (latest !== undefined) {
      const latestTick = latest.tick as number;
      const newTick = entry.tick as number;
      if (newTick <= latestTick) return false;
    }
    this.entries.push(entry);
    if (this.entries.length > RING_CAPACITY) {
      this.entries.shift();
    }
    return true;
  }

  /** Current number of retained snapshots. */
  size(): number {
    return this.entries.length;
  }

  /** Newest entry, or `undefined` if empty. */
  latest(): SnapshotEntry | undefined {
    return this.entries[this.entries.length - 1];
  }

  /** Oldest entry, or `undefined` if empty. */
  oldest(): SnapshotEntry | undefined {
    return this.entries[0];
  }

  /** All entries, ordered oldest → newest. Copy — callers may mutate. */
  toArray(): readonly SnapshotEntry[] {
    return this.entries.slice();
  }

  /**
   * Look up an interpolation pair for a target render-tick (float).
   *
   * Return value:
   *   { kind: "pair", a, b, alpha }  — normal case
   *   { kind: "single", entry }      — pre-first-snapshot, past-latest,
   *                                    empty gap, or degenerate a===b
   *   { kind: "empty" }              — buffer empty
   *
   * Semantics per doctrine — never extrapolate; clamp.
   */
  lookup(renderTick: number): SnapshotLookup {
    const n = this.entries.length;
    if (n === 0) return { kind: "empty" };
    const first = this.entries[0];
    const last = this.entries[n - 1];
    /* c8 ignore next 4 */
    if (first === undefined || last === undefined) {
      // Unreachable given n ≥ 1; keeps TS narrowing happy without `!`.
      return { kind: "empty" };
    }
    if (n === 1) return { kind: "single", entry: first };
    if (renderTick <= first.tick) return { kind: "single", entry: first };
    if (renderTick >= last.tick) return { kind: "single", entry: last };
    // Find adjacent pair a.tick ≤ renderTick ≤ b.tick.
    // Linear scan is fine at capacity 64.
    //
    // Post-conditions from the guards above:
    //   • entries.length ≥ 2, sorted strictly ascending (push rejects
    //     duplicates / staleness)
    //   • oldest < renderTick < newest
    // → exactly one bracketing pair exists; the loop always returns.
    for (let i = 0; i < n - 1; i++) {
      const a = this.entries[i];
      const b = this.entries[i + 1];
      /* c8 ignore next */
      if (a === undefined || b === undefined) continue;
      // a.tick <= renderTick is guaranteed by the ordered scan +
      // "renderTick > oldest" guard above; only b.tick needs testing.
      /* c8 ignore next 4 */
      if (renderTick > b.tick) continue;
      const alpha = (renderTick - a.tick) / (b.tick - a.tick);
      return { kind: "pair", a, b, alpha };
    }
    // Unreachable given the bracket guarantee; return newest as a
    // total-function fallback (never taken in coverage).
    /* c8 ignore next */
    return { kind: "single", entry: last };
  }
}

/** Discriminated result of `SnapshotBuffer.lookup`. */
export type SnapshotLookup =
  | { readonly kind: "empty" }
  | { readonly kind: "single"; readonly entry: SnapshotEntry }
  | {
      readonly kind: "pair";
      readonly a: SnapshotEntry;
      readonly b: SnapshotEntry;
      readonly alpha: number;
    };

/**
 * Interpolation clock — advances `renderTick` by `dt * tickRate` each
 * frame, soft-resyncs toward `latestTick - delay`, and SNAPS on
 * >3-tick drift or explicit `snap()` (visibility change).
 *
 * Wall-clock intake is dt in SECONDS — the caller passes rAF frame
 * deltas (or seconds since last update in tests). This is the
 * PRESENTATION layer; wall-clock is legal by design.
 */
export class InterpolationClock {
  private readonly tickRate: number;
  private readonly delayTicks: number;
  private renderTick: number;
  private initialized: boolean;

  constructor(params: {
    /** Sim tick rate (ticks/sec) — from tuning. */
    tickRate: number;
    /** Interpolation delay in ticks (default: DEFAULT_INTERP_DELAY_TICKS). */
    delayTicks?: number;
  }) {
    this.tickRate = params.tickRate;
    this.delayTicks = params.delayTicks ?? DEFAULT_INTERP_DELAY_TICKS;
    this.renderTick = 0;
    this.initialized = false;
  }

  /** Current render-tick (float). */
  get current(): number {
    return this.renderTick;
  }

  /** Interpolation delay in ticks (read-only after construction). */
  get delay(): number {
    return this.delayTicks;
  }

  /** True once at least one snapshot has arrived. */
  get isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Advance the clock by `dtSeconds` of wall-clock time, given the
   * current latest server tick. Idempotent when `dtSeconds === 0`.
   *
   * If not yet initialized (no snapshots seen), SNAP to the target.
   *
   * Returns the new `renderTick` (float).
   */
  step(dtSeconds: number, latestTick: number): number {
    const target = latestTick - this.delayTicks;
    if (!this.initialized) {
      this.renderTick = target;
      this.initialized = true;
      return this.renderTick;
    }
    // Advance by wall-clock.
    this.renderTick += dtSeconds * this.tickRate;
    // Soft resync toward target.
    const error = target - this.renderTick;
    if (Math.abs(error) > SNAP_ERROR_TICKS) {
      this.renderTick = target;
    } else if (error !== 0) {
      this.renderTick += error * RESYNC_NUDGE_FRACTION;
    }
    return this.renderTick;
  }

  /**
   * Snap the clock to the target (`latestTick - delay`). Called on
   * `visibilitychange` in the browser — do not animate through the gap.
   */
  snap(latestTick: number): number {
    this.renderTick = latestTick - this.delayTicks;
    this.initialized = true;
    return this.renderTick;
  }
}
